import React, { useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import {
  TicketClient,
  CREATE_MILESTONE_TASK,
  CREATE_TASKS
} from '@nile/graphql';
import {
  Icon,
  NewTask,
  CopyTask,
  NormalModal,
  getTranslatedText,
  AccessControl,
  isAuthorised,
  toast,
  ERROR_CODES,
  useProduct
} from '@nile/components';
import { DeleteModal } from '@nile/projects/src/Components/Modals';
import { Medal, User, Tasks } from '@styled-icons/fa-solid';
import {
  DELETE_MILESTONE,
  UPDATE_PROJECT,
  UPDATE_MILESTONE,
  GET_PROJECT
} from '@nile/projects/src/Graphql';
import { MoreVert } from '@styled-icons/material';
import { Popover, Button } from '@superops/ui-design-system';
import {
  constructTaskObject,
  isUnauthorizedUserPresent,
  filterAttrs,
  excludeMembers
} from '@nile/projects/src/Utils';
import { cloneDeep } from 'lodash';

import { TextCell } from '@nile/projects/src/Components/Inputs';
import { FormattedMessage, injectIntl } from 'react-intl';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';

const TitleHolder = styled.div`
  display: flex;
  padding: 10px;
  justify-content: space-between;
  align-items: center;
  .fx {
    align-items: center;
  }
  .title {
    font-size: 2rem;
    font-weight: bold;
    width: 11vw;
    .ellipsis {
      white-space: nowrap;
      line-height: 2.5rem;
    }
  }
`;

const Field = styled.div`
  min-width: 152px;
  max-width: 160px;
  flex-grow: 1;
  margin-top: 8px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .opt {
    justify-content: flex-start;

    :hover {
      background-color: #ebebfa;
    }
  }
`;

const ButtonHolder = styled.div`
  display: flex;

  > div:first-child {
    margin-right: 10px;
  }
`;

const LaneHeader = (props) => {
  const { intl } = props;
  const [sheetActive, setSheetActive] = useState(false);

  const isItProduct = useCheckIsItProduct();

  const projectContext = useContext(ProjectContext);

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };
  const isMSPProduct = useProduct('MSP');
  const [warningModal, setWarningModal] = useState(false);

  const [taskTemplates, setTaskTemplates] = useState([]);

  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopoverActive = useCallback(
    () => setPopoverActive((val) => !val),
    []
  );

  const [active, setActive] = useState(false);

  const handleChange = useCallback(() => setActive(!active), [active]);

  const delModal = React.createRef();

  const getCurrentType = () => {
    if (props?.userId || props?.isUnassociatedTasks) return 'technician';
    if (props?.milestoneId || props?.isOtherTasks) return 'milestone';
    return 'status';
  };

  const presentMembers = props.project?.members.map(
    (member) => member.user.userId
  );
  presentMembers.push(props.project?.owner.userId);

  const addTaskTemplatesHandler = (updatedTaskList) => {
    if (updatedTaskList?.length) {
      TicketClient.mutate({
        mutation: CREATE_TASKS,
        variables: { taskInput: updatedTaskList }
      })
        .then((res) => {
          if (res?.data?.createTasks) {
            props.setLanes();
            toast('success', 'Tasks added successfully');
            setTaskTemplates([]);
            setWarningModal(false);
          }
        })
        .catch((err) => {
          // if (
          //   err?.graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode
          // ) {
          //   toast(
          //     'error',
          //     <FormattedMessage
          //       id={
          //         ERROR_CODES[
          //           err.graphQLErrors[0].extensions.clientError[0].errorCode
          //         ]
          //       }
          //     />
          //   );
          // } else {
          //   // toast('error', 'Something went wrong');
          // }
        });
    }
  };

  const updateContext = (type, taskList) => {
    if (type === 'taskTemplateList') {
      let updatedTaskList = taskList.map((task) => {
        const { taskTemplateId, ...rest } = task;
        rest.project = {
          projectId: props.project.projectId
        };
        switch (getCurrentType()) {
          case 'milestone':
            if (!props?.isOtherTasks) {
              rest.milestone = {
                milestoneId: props.laneId
              };
            }
            break;
          case 'technician':
            if (!props?.isUnassociatedTasks) {
              rest.technician = { userId: props.laneId };
            }
            break;
          case 'status':
            rest.status = props.laneId;
            break;
          default:
            break;
        }
        rest.technician = filterAttrs(rest.technician);
        return rest;
      });
      if (isUnauthorizedUserPresent(presentMembers, updatedTaskList)) {
        updatedTaskList = excludeMembers(presentMembers, updatedTaskList);
        setTaskTemplates(updatedTaskList);
        setWarningModal(true);
      } else {
        addTaskTemplatesHandler(updatedTaskList);
        setTaskTemplates(updatedTaskList);
      }
    }
  };

  const context = {
    value: {
      addTaskTemplateList: [],
      removeTaskTemplateList: [],
      taskTemplateList: [],
      selectedTaskTemplates: []
    },
    updateContext: updateContext
  };

  const isOwner = () => {
    if (getCurrentType() === 'technician') {
      return props?.isOwner;
    }
    return false;
  };

  const getTaskData = () => {
    if (getCurrentType() === 'technician') {
      return { technician: { userId: props.laneId } };
    }
    if (getCurrentType() === 'status') {
      return { status: props.laneId };
    }
    return null;
  };

  const handleTechnicianRemoval = () => {
    const reqObj = {
      projectId: props.project.projectId,
      deleteMembers: [{ userId: props.userId }]
    };
    TicketClient.mutate({
      mutation: UPDATE_PROJECT,
      variables: { ...reqObj, isMSPProduct }
    })
      .then((res) => {
        if (res?.data?.updateProject) {
          props.setProject(res.data.updateProject);
          toast('success', 'Members removed successfully');
        }
      })
      .catch((err) => {
        // if (err?.graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode) {
        //   toast(
        //     'error',
        //     <FormattedMessage
        //       id={
        //         ERROR_CODES[
        //           err.graphQLErrors[0].extensions.clientError[0].errorCode
        //         ]
        //       }
        //     />
        //   );
        // } else {
        //   // toast('error', 'Something went wrong');
        // }
      });
  };

  const handleMilestoneRemoval = () => {
    TicketClient.mutate({
      mutation: DELETE_MILESTONE,
      variables: {
        milestoneId: props.milestoneId
      }
    })
      .then((res) => {
        if (res?.data?.deleteMilestone) {
          const updatedProject = cloneDeep(props.project);
          updatedProject.milestones = updatedProject.milestones.filter(
            (milesone) => milesone.milestoneId !== res.data.deleteMilestone
          );
          props.setProject(updatedProject);
          toast('success', 'Milestone deleted successfully');
        }
      })
      .catch((err) => {
        // if (err?.graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode) {
        //   toast(
        //     'error',
        //     <FormattedMessage
        //       id={
        //         ERROR_CODES[
        //           err.graphQLErrors[0].extensions.clientError[0].errorCode
        //         ]
        //       }
        //     />
        //   );
        // } else {
        //   // toast('error', 'Something went wrong');
        // }
      });
  };

  const deletionHandler = () => {
    if (getCurrentType() === 'technician') {
      return handleTechnicianRemoval();
    }
    return handleMilestoneRemoval();
  };

  const handleUpdate = (property, value) => {
    TicketClient.mutate({
      mutation: UPDATE_MILESTONE,
      variables: {
        milestoneId: props.milestoneId,
        input: { milestoneId: props.milestoneId, [property]: value }
      }
    })
      .then((res) => {
        if (res?.data?.updateMilestone) {
          TicketClient.query({
            query: GET_PROJECT,
            variables: { projectId: props.project.projectId, isItProduct },
            fetchPolicy: 'network-only'
          }).then((resp) => {
            if (resp?.data?.getProject) {
              toast('success', 'Milestone updated successfully');

              props.setProject(resp.data.getProject);
            }
          });
        }
      })
      .catch((err) => {
        // if (err?.graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode) {
        //   toast(
        //     'error',
        //     <FormattedMessage
        //       id={
        //         ERROR_CODES[
        //           err.graphQLErrors[0].extensions.clientError[0].errorCode
        //         ]
        //       }
        //     />
        //   );
        // } else {
        //   // toast('error', 'Something went wrong');
        // }
      });
  };

  const getMilestoneObject = () => {
    if (getCurrentType() === 'milestone' && !props?.isOtherTasks) {
      return { milestone: { milestoneId: props.laneId } };
    }
    return {};
  };

  const handleTaskAddition = (type, task) => {
    const taskObj = constructTaskObject(task);
    TicketClient.mutate({
      mutation: CREATE_MILESTONE_TASK,
      variables: {
        ...taskObj,
        project: { projectId: props.project.projectId },
        ...getMilestoneObject()
      }
    })
      .then((res) => {
        if (res?.data?.createTask) {
          props.setLanes();
          toast('success', 'Task created successfully');
          toggleSheetActive();
        }
      })
      .catch((err) => {
        // if (err?.graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode) {
        //   toast(
        //     'error',
        //     <FormattedMessage
        //       id={
        //         ERROR_CODES[
        //           err.graphQLErrors[0].extensions.clientError[0].errorCode
        //         ]
        //       }
        //     />
        //   );
        // } else {
        //   // toast('error', 'Something went wrong');
        // }
      });
  };

  const renderIcon = () => {
    if (props?.userId) {
      return (
        <Icon
          style={{
            width: 20,
            height: 20,
            color: '#252739',
            marginRight: '10px'
          }}
          icon={User}
        />
      );
    }
    if (props?.milestoneId) {
      return (
        <Icon
          style={{
            width: 20,
            height: 20,
            color: '#252739',
            marginRight: '10px'
          }}
          icon={Medal}
        />
      );
    }
    if (props?.state || props?.isOtherTasks || props?.isUnassociatedTasks) {
      return (
        <Icon
          style={{
            width: 20,
            height: 20,
            color: '#252739',
            marginRight: '10px'
          }}
          icon={Tasks}
        />
      );
    }
    return null;
  };

  const activator = (
    <Button
      onClick={togglePopoverActive}
      plain
      icon={
        <Icon
          style={{
            width: 20,
            height: 20,
            color: '#252739'
          }}
          icon={MoreVert}
        />
      }
    />
  );

  const DeleteBtn = () => (
    <div data-qa="deleteKanbanBtn" className="opt full-width">
      <Button ref={delModal} onClick={handleChange} destructive plain fullWidth>
        {`${getTranslatedText(intl, 'common.delete')} ${getCurrentType()}`}
      </Button>
    </div>
  );

  return (
    <>
      <TitleHolder>
        <div className="fx">
          {renderIcon()}
          <div className="title">
            <TextCell
              value={props.title}
              handleUpdate={handleUpdate}
              property="name"
              className="ellipsis mw200"
            />
          </div>
        </div>
        {(isAuthorised(projectContext.features, 'task.add') ||
          isAuthorised(
            projectContext.features,
            'projectManagementProject.edit'
          ) ||
          isAuthorised(projectContext.features, 'milestone.delete')) &&
          !props?.isUnassociatedTasks && (
            <ButtonHolder data-qa="kanbanOptions" className="options">
              <Popover
                active={popoverActive}
                activator={activator}
                onClose={togglePopoverActive}
              >
                <Field>
                  <AccessControl
                    reference={projectContext.features}
                    roleToCheck="task.add"
                    render={
                      <>
                        <div
                          data-qa="addKanbanTaskBtn"
                          className="opt full-width"
                        >
                          <Button onClick={toggleSheetActive} plain fullWidth>
                            <FormattedMessage id="project.details.actions.addTask" />
                          </Button>
                        </div>
                        <AccessControl
                          reference={projectContext.features}
                          roleToCheck="taskTemplate.view"
                          render={
                            <div
                              data-qa="addKanbanTaskTemplateBtn"
                              className="opt full-width"
                            >
                              <CopyTask
                                tasksList={
                                  props?.taskTemplates?.getTaskTemplateList
                                }
                                context={context}
                                name={getTranslatedText(
                                  intl,
                                  'project.details.actions.template'
                                )}
                                hideIcon
                                isLink
                              />
                            </div>
                          }
                        />
                      </>
                    }
                  />
                  {getCurrentType() !== 'status' &&
                    !isOwner() &&
                    !props?.isOtherTasks &&
                    (getCurrentType() === 'technician' ? (
                      <AccessControl
                        reference={projectContext.features}
                        roleToCheck="projectManagementProject.edit"
                        render={<DeleteBtn />}
                      />
                    ) : (
                      <AccessControl
                        reference={projectContext.features}
                        roleToCheck="milestone.delete"
                        render={<DeleteBtn />}
                      />
                    ))}
                </Field>
              </Popover>
            </ButtonHolder>
          )}
      </TitleHolder>
      <NewTask
        addOrUpdateTechGroup={handleTaskAddition}
        state={sheetActive}
        toggleSheetActive={toggleSheetActive}
        name={getTranslatedText(intl, 'project.details.actions.addTask')}
        hideIcon
        isLink
        noRender
        data={getTaskData()}
        module="PROJECT_TASK"
        details={{ operation: 'CREATE', projectId: props?.project?.projectId }}
      />
      <DeleteModal
        description={`${getTranslatedText(
          intl,
          'project.details.delete.this'
        )} ${getCurrentType()}?`}
        handleDeleteConfirmation={deletionHandler}
        title={`${getTranslatedText(
          intl,
          'common.delete'
        )} ${getCurrentType()}`}
        reference={delModal}
        active={active}
        handleChange={handleChange}
      />
      <NormalModal
        isOpen={warningModal}
        toggleOpen={setWarningModal}
        title={getTranslatedText(
          intl,
          'project.details.actions.templateWarnings'
        )}
        primaryBtn={getTranslatedText(intl, 'common.confirm')}
        secondaryBtn={getTranslatedText(intl, 'common.cancel')}
        primaryAction={() => addTaskTemplatesHandler(taskTemplates)}
        description={getTranslatedText(intl, 'project.details.actions.desc')}
      />
    </>
  );
};

export default injectIntl(LaneHeader);
